<template>
  <div class="army-preview">
    <header class="army-preview__head">
      <h2 class="army-preview__name">{{ army.name || '(unnamed army)' }}</h2>
      <div class="army-preview__meta">
        <span>{{ army.alliance || 'Unknown alliance' }}</span>
        <span v-if="army.keyword">· {{ army.keyword }}</span>
        <span v-if="army.isArmyOfRenown">· Army of Renown</span>
        <span v-if="army.legends">· Legends</span>
      </div>
    </header>

    <!-- Traits + formations -->
    <section v-for="s in topSections" :key="s.heading" class="army-preview__section">
      <div class="st-eyebrow">{{ s.heading }}</div>
      <GroupPreview v-for="g in s.groups" :key="g.id" :group="g" :hide-name="s.hideNames" />
    </section>

    <!-- Enhancements: category → table (name + restriction) → upgrades -->
    <section v-if="pathBlocks.length" class="army-preview__section">
      <div class="st-eyebrow">Paths</div>
      <PathPreview v-for="path in pathBlocks" :key="path.id || path.name" :path="path" />
    </section>

    <!-- Enhancements: category → table (name + restriction) → upgrades -->
    <section v-for="c in enhancementBlocks" :key="c.category" class="army-preview__section">
      <div class="st-eyebrow">{{ c.category }}</div>
      <div v-for="t in c.tables" :key="t.name" class="army-preview__table">
        <div class="army-preview__table-name">{{ t.name }}</div>
        <div
          v-if="t.restrictionText"
          class="army-preview__restriction"
          v-html="formatText(t.restrictionText)"
        ></div>
        <GroupPreview v-for="u in t.upgrades" :key="u.id" :group="u" hide-name show-points />
      </div>
    </section>

    <!-- Lores -->
    <section v-for="s in loreSections" :key="s.heading" class="army-preview__section">
      <div class="st-eyebrow">{{ s.heading }}</div>
      <GroupPreview v-for="g in s.groups" :key="g.id" :group="g" />
    </section>

    <p v-if="isEmpty" class="army-preview__empty">
      Nothing authored yet — add traits, formations, lores, or enhancements.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { AuthoredArmy, IPath, IUpgrade } from '@scrolldex/shared';
import GroupPreview from './GroupPreview.vue';
import PathPreview from './PathPreview.vue';
import { formatText } from '../../utils/formatter.js';

const props = defineProps<{ army: AuthoredArmy }>();

interface PreviewSection {
  heading: string;
  groups: IUpgrade[];
  // Suppress each group's own name (when it would just repeat the heading, e.g.
  // the single Battle Traits group).
  hideNames?: boolean;
}
interface EnhancementTable {
  name: string;
  restrictionText?: string;
  upgrades: IUpgrade[];
}
interface EnhancementBlock {
  category: string;
  tables: EnhancementTable[];
}

function hasContent(g?: IUpgrade): g is IUpgrade {
  return !!g && (g.abilities.length > 0 || g.rules.length > 0);
}
function withContent(list?: IUpgrade[]): IUpgrade[] {
  return (list ?? []).filter(hasContent);
}
function pathHasContent(path: IPath): boolean {
  return (
    path.requirements.keywords.length > 0 ||
    path.requirements.nonKeywords.length > 0 ||
    path.aspiring.options.length > 0 ||
    path.elite.options.length > 0 ||
    path.mighty.options.length > 0 ||
    path.legendary.options.length > 0
  );
}

// Units have their own per-unit warscroll preview, so they're not repeated here.
const topSections = computed<PreviewSection[]>(() => {
  const a = props.army;
  const traits = hasContent(a.battleTraits?.[0]) ? [a.battleTraits![0]] : [];
  return [
    { heading: 'Battle Traits', groups: traits, hideNames: true },
    { heading: 'Battle Formations', groups: withContent(a.battleFormations) },
  ].filter((s) => s.groups.length > 0);
});

const loreSections = computed<PreviewSection[]>(() => {
  const a = props.army;
  return [
    { heading: 'Spell Lores', groups: withContent(a.lores?.spell) },
    { heading: 'Prayer Lores', groups: withContent(a.lores?.prayer) },
    { heading: 'Manifestation Lores', groups: withContent(a.lores?.manifestation) },
  ].filter((s) => s.groups.length > 0);
});

const pathBlocks = computed(() => (props.army.paths ?? []).filter(pathHasContent));

// Each enhancement category with its tables that hold at least one real upgrade.
const enhancementBlocks = computed<EnhancementBlock[]>(() => {
  const out: EnhancementBlock[] = [];
  for (const [category, tables] of Object.entries(props.army.enhancements ?? {})) {
    const withUpgrades: EnhancementTable[] = [];
    for (const t of tables ?? []) {
      const upgrades = withContent(t.upgrades);
      if (upgrades.length) {
        withUpgrades.push({ name: t.name, restrictionText: t.restrictionText, upgrades });
      }
    }
    if (withUpgrades.length) out.push({ category, tables: withUpgrades });
  }
  return out;
});

const isEmpty = computed(
  () =>
    !topSections.value.length &&
    !pathBlocks.value.length &&
    !loreSections.value.length &&
    !enhancementBlocks.value.length,
);
</script>

<style scoped>
.army-preview {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
.army-preview__head {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-1);
}
.army-preview__name {
  font-size: var(--st-text-xl);
  font-weight: var(--st-weight-bold);
}
.army-preview__meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--st-space-2);
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
}
.army-preview__section {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.army-preview__table {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.army-preview__table-name {
  font-weight: var(--st-weight-semibold);
  font-size: var(--st-text-sm);
}
.army-preview__restriction {
  font-size: var(--st-text-sm);
  font-style: italic;
  color: var(--st-text-muted);
}
.army-preview__empty {
  color: var(--st-text-muted);
  font-size: var(--st-text-sm);
}
</style>
